import { Button, Tooltip, Spinner } from '@chakra-ui/react';
import { FC, useState } from 'react';

import BookmarkFilledIcon from '@/assets/icons/ic-bookmark-filled.svg';
import BookmarkIcon from '@/assets/icons/ic-bookmark.svg';

import { IToastType } from '@/enums/ToastType';
import useSession from '@/hooks/useSession';
import { useStores } from '@/hooks/useStores';
import { useToast } from '@/hooks/useToast';
import { AgendaLecture } from '@/resources/AgendaLecture';
import { ThinLecture } from '@/resources/ThinLecture';
import { ThinProfile } from '@/resources/ThinProfile';

interface ISaveLectureButton {
  lecture: ThinLecture;
  mutate: () => void;
  eventId: string;
}

export const SaveLectureButton: FC<ISaveLectureButton> = ({ lecture, mutate, eventId }) => {
  const { store } = useStores();
  const { thinProfile, user } = useSession();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  async function handleBookmarkClick(e: React.MouseEvent) {
    e.preventDefault();
    try {
      setIsLoading(true);
      if (lecture.agendaLectures?.length > 0) {
        const agendaLecture = lecture.agendaLectures[0];

        await removeFromBookmark(agendaLecture.id);
      } else {
        await addToBookmark();
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function addToBookmark() {
    try {
      await store.request(`${ThinProfile.type}/${thinProfile.id}/${AgendaLecture.type}`, 'POST', {
        data: {
          type: AgendaLecture.type,
          attributes: {
            event_id: eventId,
            lecture_id: lecture.id,
          },
        },
      });
      await mutate();
      toast({ status: IToastType.INFO, title: 'Presentation saved to My Agenda' });
    } catch (error) {
      toast({ status: IToastType.ERROR, title: 'Error adding to agenda' });
    }
  }

  async function removeFromBookmark(agenda_id: string | null) {
    if (!agenda_id) return;
    try {
      await store.request(`${ThinProfile.type}/${thinProfile.id}/${AgendaLecture.type}/${agenda_id}`, 'DELETE');
      await mutate();
      toast({ status: IToastType.INFO, title: 'Presentation removed from My Agenda' });
    } catch (error) {
      toast({ status: IToastType.ERROR, title: 'Error adding to bookmarks' });
    }
  }
  const tooltipLabel = lecture.agendaLectures?.length > 0 ? 'remove from my agenda' : 'add to my agenda';

  if (isLoading) return <Spinner speed="0.9s" size="md" />;

  return (
    <Tooltip label={tooltipLabel} isDisabled={!user} placement="top">
      <Button
        onClick={handleBookmarkClick}
        display="flex"
        justifyContent="center"
        alignItems="center"
        size="xs"
        isDisabled={!user}
        w="16px"
        variant="unstyled"
        mt="0.5"
      >
        {lecture.agendaLectures?.length > 0 ? (
          <BookmarkFilledIcon css={{ width: '18px' }} />
        ) : (
          <BookmarkIcon css={{ width: '18px' }} />
        )}
      </Button>
    </Tooltip>
  );
};
